package com.tovtalk.ui.screen.onboarding

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.tovtalk.ui.adaptive.isCompact
import com.tovtalk.ui.adaptive.isMobile
import com.tovtalk.ui.adaptive.rememberScreenWidthDp
import com.tovtalk.ui.components.BluePrimaryButton

private enum class LayoutMode { Portrait, Landscape, Card }

@Composable
private fun resolveLayoutMode(): LayoutMode {
    val widthDp by rememberScreenWidthDp()
    return when {
        isCompact() -> LayoutMode.Portrait
        isMobile() -> if (widthDp > 700f) LayoutMode.Landscape else LayoutMode.Portrait
        else -> LayoutMode.Card
    }
}

@Composable
internal fun OnboardingLayout(
    progressCurrent: Int,
    progressTotal: Int,
    onSkip: (() -> Unit)? = null,
    showSkip: Boolean = true,
    onBack: (() -> Unit)? = null,
    ctaText: String,
    onCta: () -> Unit,
    ctaEnabled: Boolean = true,
    headerContent: @Composable ColumnScope.() -> Unit,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    val mode = resolveLayoutMode()

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(OnboardingBg),
    ) {
        when (mode) {
            LayoutMode.Portrait -> PortraitLayout(
                progressCurrent = progressCurrent,
                progressTotal = progressTotal,
                onSkip = onSkip ?: {},
                showSkip = showSkip,
                onBack = onBack,
                ctaText = ctaText,
                onCta = onCta,
                ctaEnabled = ctaEnabled,
                headerContent = headerContent,
                content = content,
            )

            LayoutMode.Landscape -> LandscapeLayout(
                progressCurrent = progressCurrent,
                progressTotal = progressTotal,
                onSkip = onSkip ?: {},
                showSkip = showSkip,
                onBack = onBack,
                ctaText = ctaText,
                onCta = onCta,
                ctaEnabled = ctaEnabled,
                headerContent = headerContent,
                content = content,
            )

            LayoutMode.Card -> CardLayout(
                progressCurrent = progressCurrent,
                progressTotal = progressTotal,
                onSkip = onSkip ?: {},
                showSkip = showSkip,
                onBack = onBack,
                ctaText = ctaText,
                onCta = onCta,
                ctaEnabled = ctaEnabled,
                headerContent = headerContent,
                content = content,
            )
        }
    }
}

// --- Portrait: Column — progress bar → header → scrollable content → CTA at bottom ---

@Composable
private fun PortraitLayout(
    progressCurrent: Int,
    progressTotal: Int,
    onSkip: () -> Unit,
    showSkip: Boolean,
    onBack: (() -> Unit)?,
    ctaText: String,
    onCta: () -> Unit,
    ctaEnabled: Boolean,
    headerContent: @Composable ColumnScope.() -> Unit,
    content: @Composable () -> Unit,
) {
    Column(modifier = Modifier.fillMaxSize()) {
        OnboardingTopBar(
            current = progressCurrent,
            total = progressTotal,
            onSkip = onSkip,
            showSkip = showSkip,
            onBack = onBack,
        )
        Column(modifier = Modifier.fillMaxWidth()) {
            headerContent()
        }
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState()),
        ) {
            content()
        }
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 24.dp, vertical = 16.dp),
        ) {
            BluePrimaryButton(
                text = ctaText,
                onClick = onCta,
                enabled = ctaEnabled,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

// --- Landscape: Row — left (progress + header + CTA), right (scrollable content) ---

@Composable
private fun LandscapeLayout(
    progressCurrent: Int,
    progressTotal: Int,
    onSkip: () -> Unit,
    showSkip: Boolean,
    onBack: (() -> Unit)?,
    ctaText: String,
    onCta: () -> Unit,
    ctaEnabled: Boolean,
    headerContent: @Composable ColumnScope.() -> Unit,
    content: @Composable () -> Unit,
) {
    Row(modifier = Modifier.fillMaxSize()) {
        // Left panel: progress + header + CTA
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight()
                .padding(horizontal = 24.dp, vertical = 16.dp),
        ) {
            OnboardingTopBar(
                current = progressCurrent,
                total = progressTotal,
                onSkip = onSkip,
                showSkip = showSkip,
                onBack = onBack,
            )
            Column(modifier = Modifier.fillMaxWidth()) {
                headerContent()
            }
            Spacer(Modifier.weight(1f))
            BluePrimaryButton(
                text = ctaText,
                onClick = onCta,
                enabled = ctaEnabled,
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(Modifier.height(16.dp))
        }

        // Right panel: scrollable content
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 24.dp, vertical = 24.dp),
        ) {
            content()
        }
    }
}

// --- Card: Centered card (~480dp) on dark bg ---

private val CardShape = RoundedCornerShape(20.dp)
private val CardBg = Color.White.copy(alpha = 0.03f)
private val CardBorder = Color.White.copy(alpha = 0.06f)

@Composable
private fun CardLayout(
    progressCurrent: Int,
    progressTotal: Int,
    onSkip: () -> Unit,
    showSkip: Boolean,
    onBack: (() -> Unit)?,
    ctaText: String,
    onCta: () -> Unit,
    ctaEnabled: Boolean,
    headerContent: @Composable ColumnScope.() -> Unit,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .widthIn(max = 480.dp)
                .fillMaxWidth()
                .padding(horizontal = 24.dp)
                .clip(CardShape)
                .background(CardBg)
                .border(width = 1.dp, color = CardBorder, shape = CardShape)
                .padding(vertical = 8.dp),
        ) {
            OnboardingTopBar(
                current = progressCurrent,
                total = progressTotal,
                onSkip = onSkip,
                showSkip = showSkip,
                onBack = onBack,
            )
            Column(modifier = Modifier.fillMaxWidth()) {
                headerContent()
            }
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .weight(1f, fill = false),
            ) {
                content()
            }
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 24.dp, vertical = 16.dp),
            ) {
                BluePrimaryButton(
                    text = ctaText,
                    onClick = onCta,
                    enabled = ctaEnabled,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}
